import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Building2, MapPin, Plus, Trash2, Layers } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { Card, Button } from './index';
import api from '../services/api';

export interface EmployerLocation {
  id: string;
  naam: string;
  adres: string | null;
  postcode: string | null;
  plaats: string | null;
  type_locatie: 'kantoor' | 'productie' | 'logistiek' | 'overig';
  is_hoofdvestiging: boolean;
}

export interface EmployerDepartment {
  id: string;
  naam: string;
  location_id: string | null;
  location_naam: string | null;
}

export const LOCATION_TYPE_LABELS: Record<string, string> = {
  kantoor: 'Kantoor',
  productie: 'Productie / Fabriek',
  logistiek: 'Logistiek / Warehouse',
  overig: 'Overig',
};

const LEGE_VESTIGING = {
  naam: '',
  adres: '',
  postcode: '',
  plaats: '',
  type_locatie: 'kantoor',
  is_hoofdvestiging: false,
};

/**
 * Tab 1 (Organisatieprofiel): Vestigingen-Matrix + dynamische afdelingenlijst.
 * Vestigingen voeden de geofence per projectlocatie; afdelingen zijn universeel
 * of exclusief aan een vestiging gekoppeld (hybride matrix-koppeling). Samen
 * vormen ze de Toegangs-Scope voor het uitnodigen van collega's (tab 2).
 */
export const EmployerOrgStructuur: React.FC = () => {
  const { t } = useTranslation('employer');
  const queryClient = useQueryClient();
  const [vestigingForm, setVestigingForm] = useState({ ...LEGE_VESTIGING });
  const [toonVestigingForm, setToonVestigingForm] = useState(false);
  const [afdelingNaam, setAfdelingNaam] = useState('');
  const [afdelingVestiging, setAfdelingVestiging] = useState<string>('');

  const { data: locations = [] } = useQuery({
    queryKey: ['employer-locations'],
    queryFn: async () => (await api.get('/employer/locations')).data?.data as EmployerLocation[],
  });

  const { data: departments = [] } = useQuery({
    queryKey: ['employer-departments'],
    queryFn: async () => (await api.get('/employer/departments')).data?.data as EmployerDepartment[],
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['employer-locations'] });
    queryClient.invalidateQueries({ queryKey: ['employer-departments'] });
  };

  const addLocation = useMutation({
    mutationFn: async () =>
      api.post('/employer/locations', {
        ...vestigingForm,
        adres: vestigingForm.adres || null,
        postcode: vestigingForm.postcode || null,
        plaats: vestigingForm.plaats || null,
      }),
    onSuccess: () => {
      toast.success(t('orgStructuur.vestigingToegevoegd', 'Vestiging toegevoegd'));
      setVestigingForm({ ...LEGE_VESTIGING });
      setToonVestigingForm(false);
      invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.errors?.[0] || err?.response?.data?.message || t('orgStructuur.opslaanMislukt', 'Opslaan mislukt')),
  });

  const deleteLocation = useMutation({
    mutationFn: async (id: string) => api.delete(`/employer/locations/${id}`),
    onSuccess: () => {
      toast.success(t('orgStructuur.vestigingVerwijderd', 'Vestiging verwijderd'));
      invalidate();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || t('orgStructuur.verwijderenMislukt', 'Verwijderen mislukt')),
  });

  const addDepartment = useMutation({
    mutationFn: async () =>
      api.post('/employer/departments', {
        naam: afdelingNaam,
        location_id: afdelingVestiging || null,
      }),
    onSuccess: () => {
      toast.success(t('orgStructuur.afdelingToegevoegd', 'Afdeling toegevoegd'));
      setAfdelingNaam('');
      setAfdelingVestiging('');
      invalidate();
    },
    onError: (err: any) =>
      toast.error(err?.response?.data?.errors?.[0] || err?.response?.data?.message || t('orgStructuur.opslaanMislukt', 'Opslaan mislukt')),
  });

  const deleteDepartment = useMutation({
    mutationFn: async (id: string) => api.delete(`/employer/departments/${id}`),
    onSuccess: () => {
      toast.success(t('orgStructuur.afdelingVerwijderd', 'Afdeling verwijderd'));
      invalidate();
    },
    onError: (err: any) => toast.error(err?.response?.data?.message || t('orgStructuur.verwijderenMislukt', 'Verwijderen mislukt')),
  });

  const inputClass =
    'w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm';

  return (
    <div className="space-y-6 mt-6">
      {/* ── Vestigingen-Matrix ── */}
      <Card>
        <div className="flex items-start justify-between flex-wrap gap-3 mb-1">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Building2 className="w-5 h-5 text-blue-600" />
            {t('orgStructuur.vestigingenTitel', 'Vestigingen')}
          </h2>
          <Button size="sm" variant="outline" onClick={() => setToonVestigingForm((v) => !v)}>
            <Plus className="w-4 h-4 mr-1" />
            {t('orgStructuur.vestigingToevoegen', 'Vestiging toevoegen')}
          </Button>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          {t(
            'orgStructuur.vestigingenIntro',
            'De postcode per vestiging voedt de reisradius-matching: professionals worden gezocht rondom de vestiging waar het project plaatsvindt, niet blind vanaf het hoofdkantoor.'
          )}
        </p>

        {toonVestigingForm && (
          <form
            className="grid grid-cols-1 sm:grid-cols-2 gap-3 p-4 border border-blue-200 bg-blue-50 rounded-lg mb-4"
            onSubmit={(e) => {
              e.preventDefault();
              if (!vestigingForm.naam.trim()) {
                toast.error(t('orgStructuur.naamVerplicht', 'Locatienaam is verplicht'));
                return;
              }
              addLocation.mutate();
            }}
          >
            <div className="sm:col-span-2">
              <label className="block text-xs font-medium text-gray-700 mb-1">
                {t('orgStructuur.locatienaam', 'Locatienaam')}
              </label>
              <input
                type="text"
                className={inputClass}
                placeholder={t('orgStructuur.locatienaamPlaceholder', 'bijv. Hoofdkantoor Amsterdam / Distributiecentrum Eindhoven')}
                value={vestigingForm.naam}
                onChange={(e) => setVestigingForm((p) => ({ ...p, naam: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('orgStructuur.postcode', 'Postcode')}</label>
              <input
                type="text"
                className={inputClass}
                placeholder="1234AB"
                value={vestigingForm.postcode}
                onChange={(e) => setVestigingForm((p) => ({ ...p, postcode: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('orgStructuur.plaats', 'Plaats')}</label>
              <input
                type="text"
                className={inputClass}
                value={vestigingForm.plaats}
                onChange={(e) => setVestigingForm((p) => ({ ...p, plaats: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('orgStructuur.adres', 'Adres')}</label>
              <input
                type="text"
                className={inputClass}
                value={vestigingForm.adres}
                onChange={(e) => setVestigingForm((p) => ({ ...p, adres: e.target.value }))}
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1">{t('orgStructuur.typeLocatie', 'Type locatie')}</label>
              <select
                className={inputClass}
                value={vestigingForm.type_locatie}
                onChange={(e) => setVestigingForm((p) => ({ ...p, type_locatie: e.target.value }))}
              >
                {Object.entries(LOCATION_TYPE_LABELS).map(([key, label]) => (
                  <option key={key} value={key}>{label}</option>
                ))}
              </select>
            </div>
            <label className="flex items-center gap-2 text-sm text-gray-700 sm:col-span-2">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                checked={vestigingForm.is_hoofdvestiging}
                onChange={(e) => setVestigingForm((p) => ({ ...p, is_hoofdvestiging: e.target.checked }))}
              />
              {t('orgStructuur.hoofdvestiging', 'Dit is de hoofdvestiging')}
            </label>
            <div className="sm:col-span-2 flex gap-2">
              <Button type="submit" size="sm" loading={addLocation.isPending}>
                {t('orgStructuur.opslaan', 'Opslaan')}
              </Button>
              <Button type="button" size="sm" variant="outline" onClick={() => setToonVestigingForm(false)}>
                {t('orgStructuur.annuleren', 'Annuleren')}
              </Button>
            </div>
          </form>
        )}

        {locations.length === 0 ? (
          <p className="text-sm text-gray-500 py-4 text-center">
            {t('orgStructuur.geenVestigingen', 'Nog geen vestigingen — voeg uw eerste locatie toe.')}
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('orgStructuur.colNaam', 'Naam')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('orgStructuur.colAdres', 'Adres')}</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">{t('orgStructuur.colType', 'Type')}</th>
                  <th className="px-4 py-2 text-right text-xs font-medium text-gray-500 uppercase" />
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {locations.map((loc) => (
                  <tr key={loc.id}>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900 whitespace-nowrap">
                      {loc.naam}
                      {loc.is_hoofdvestiging && (
                        <span className="ml-2 inline-flex px-2 py-0.5 text-xs font-medium rounded-full bg-blue-100 text-blue-800">
                          {t('orgStructuur.hoofdBadge', 'Hoofdvestiging')}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        <MapPin className="w-3.5 h-3.5 text-gray-400" />
                        {[loc.adres, loc.postcode, loc.plaats].filter(Boolean).join(', ') ||
                          t('orgStructuur.geenAdres', 'geen adres')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 whitespace-nowrap">
                      {LOCATION_TYPE_LABELS[loc.type_locatie] || loc.type_locatie}
                    </td>
                    <td className="px-4 py-3 text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          if (window.confirm(t('orgStructuur.vestigingVerwijderConfirm', 'Vestiging "{{naam}}" verwijderen? Gekoppelde afdelingen worden ook gedeactiveerd.', { naam: loc.naam }))) {
                            deleteLocation.mutate(loc.id);
                          }
                        }}
                        disabled={deleteLocation.isPending}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* ── Afdelingen ── */}
      <Card>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-1">
          <Layers className="w-5 h-5 text-blue-600" />
          {t('orgStructuur.afdelingenTitel', 'Afdelingen')}
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          {t(
            'orgStructuur.afdelingenIntro',
            'Afdelingen zijn universeel (hele organisatie) of exclusief gekoppeld aan één vestiging. Samen met vestigingen bepalen zij de Toegangs-Scope bij het uitnodigen van collega’s.'
          )}
        </p>

        <form
          className="flex flex-wrap gap-2 mb-4"
          onSubmit={(e) => {
            e.preventDefault();
            if (!afdelingNaam.trim()) {
              toast.error(t('orgStructuur.afdelingNaamVerplicht', 'Afdelingsnaam is verplicht'));
              return;
            }
            addDepartment.mutate();
          }}
        >
          <input
            type="text"
            className={`${inputClass} flex-1 min-w-[180px]`}
            placeholder={t('orgStructuur.afdelingPlaceholder', 'bijv. Logistiek, ICT, Klantenservice')}
            value={afdelingNaam}
            onChange={(e) => setAfdelingNaam(e.target.value)}
          />
          <select
            className={`${inputClass} w-auto`}
            value={afdelingVestiging}
            onChange={(e) => setAfdelingVestiging(e.target.value)}
          >
            <option value="">{t('orgStructuur.universeel', 'Universeel (alle vestigingen)')}</option>
            {locations.map((loc) => (
              <option key={loc.id} value={loc.id}>{loc.naam}</option>
            ))}
          </select>
          <Button type="submit" size="sm" loading={addDepartment.isPending}>
            <Plus className="w-4 h-4 mr-1" />
            {t('orgStructuur.afdelingToevoegen', 'Voeg afdeling toe')}
          </Button>
        </form>

        {departments.length === 0 ? (
          <p className="text-sm text-gray-500 py-2 text-center">
            {t('orgStructuur.geenAfdelingen', 'Nog geen afdelingen toegevoegd.')}
          </p>
        ) : (
          <div className="flex flex-wrap gap-2">
            {departments.map((dep) => (
              <span
                key={dep.id}
                className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-gray-200 bg-gray-50 text-sm text-gray-800"
              >
                {dep.naam}
                <span className="text-xs text-gray-500">
                  {dep.location_naam || t('orgStructuur.universeelKort', 'universeel')}
                </span>
                <button
                  type="button"
                  className="text-gray-400 hover:text-red-600"
                  onClick={() => {
                    if (window.confirm(t('orgStructuur.afdelingVerwijderConfirm', 'Afdeling "{{naam}}" verwijderen?', { naam: dep.naam }))) {
                      deleteDepartment.mutate(dep.id);
                    }
                  }}
                >
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </span>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
};
